import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import moment from 'moment';
import { useChecklists } from 'src/hooks/checklist';
import { useAddChecklistToJob } from 'src/hooks/job';
import { notification } from 'src/components/Notification';
import Button from 'src/components/Button';
import logger from 'src/utils/logger';
import SelectBox from 'src/components/SelectBox';
import Styles from 'src/components/AddChecklistToJobPopup/AddChecklistToJobPopup.styles';

const AddChecklistToJobPopup = (props) => {
  const {
    jobId,
    closeHandler,
    onContinue,
  } = props;

  const [requestPayload, setRequestPayload] = useState({
    jobToId: jobId,
    checklistId: null,
  });

  const [expanded, setExpanded] = useState(false);

  const { data, loading: loadingChecklists } = useChecklists({
    limit: 9999,
    offset: 0,
    orderBy: 'name',
    orderDirection: 'asc',
  });
  const [processing, setProcessing] = useState(loadingChecklists);

  const menuOpenCloseHandlers = useMemo(() => ({
    onMenuOpen: () => {
      setExpanded(true);
    },
    onMenuClose: () => {
      setExpanded(false);
    },
  }), [true]);

  const checklistOptionList = useMemo(() => {
    setProcessing(loadingChecklists);
    return (data?.checklists || [])
      .filter(checklist => checklist.currentVersion.status === 'published' &&
        moment.utc(new Date(checklist.currentVersion.effectiveDate))
          .isBefore(moment.utc(new Date())),
      )
      .map((checklist) => ({
        value: checklist.id,
        label: checklist.currentVersion.name,
      }));
  }, [loadingChecklists]);

  const { t } = useTranslation();


  const addChecklistToJob = useAddChecklistToJob(jobId);

  const addChecklist = async () => {
    try {
      const {
        data: {
          addChecklistToJob: {
            success,
            error,
          },
        },
      } = await addChecklistToJob({ ...requestPayload });
      if (!success || error) {
        if(error === 'invalid checklist effective date') {
          notification.error(t('ADDING_CHECKLIST_TO_JOB_FAILED_BECAUSE_OF_INVALID_CHECKLIST_EFFECTIVE_DATE'))
         }
         else {
          notification.error(t('ADDING_CHECKLIST_TO_JOB_FAILED'));
         }
      } else {
        notification.success(t('ADDED_CHECKLIST_TO_JOB'));
        onContinue();
      }
    } catch (err) {
      logger.error(err);
      console.log("error in catch block",err)
      notification.error(t('ADDING_CHECKLIST_TO_JOB_FAILED'));
    } finally {
      if(closeHandler && typeof closeHandler === 'function') {
        closeHandler();
      }
    }
  };

  return (
    <Styles.AddChecklistPopup expanded={expanded} onClose={closeHandler}>
      <Styles.PopupContent>
        <p>{t('ADD_CHECKLIST_SUGGESTION_TEXT')}</p>
        <br />
        <p>{t('ADD_CHECKLIST_SELECTION_LIST_LABEL')}</p>
        <SelectBox
          {...menuOpenCloseHandlers}
          isDisabled={processing}
          isLoading={processing}
          maxMenuHeight={'15rem'}
          minMenuHeight={'15rem'}
          onChange={(selectedOption) => {
            setRequestPayload({ ...requestPayload, checklistId: selectedOption.value });
          }}
          options={checklistOptionList}
          required={true}
        />
      </Styles.PopupContent>
      <Styles.ActionButtonRow expanded={expanded}>
        <Button
          className={!requestPayload.checklistId || processing ? '' : 'enableBtn'}
          disabled={!requestPayload.checklistId || processing}
          gray={!requestPayload.checklistId || processing}
          onClick={() => { 
            setProcessing(true);
            addChecklist();
          }}
          sm
          type="button">
          {t('ADD_CHECKLIST')}
        </Button>
        <Button
          className={!!requestPayload.checklistId || processing ? '' : 'enableBtn'}
          disabled={!!requestPayload.checklistId || processing}
          gray={!!requestPayload.checklistId || processing}
          onClick={onContinue}
          sm
          type="button">
          {t('CONTINUE_WITHOUT_CHECKLIST')}
        </Button>
      </Styles.ActionButtonRow>
    </Styles.AddChecklistPopup>);
};

AddChecklistToJobPopup.propTypes = {
  closeHandler: PropTypes.func.isRequired,
  jobId: PropTypes.number.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default React.memo(AddChecklistToJobPopup);
